import type { AppI18nProvider } from './AppI18nProvider'

/**
 * AppI18nT - Web Component for displaying translations
 *
 * Usage:
 * <app-i18n-t key="foo.bar"></app-i18n-t>
 * <app-i18n-t key="question_progress" vars='{"current": 2, "total": 5}'></app-i18n-t>
 */

export class AppI18nT extends HTMLElement {
  static observedAttributes = ['key', 'vars']
  #provider: AppI18nProvider | null = null

  constructor() {
    super()
  }

  connectedCallback() {
    this.#findProvider()
    this.#render()
  }

  attributeChangedCallback(_name: string, _oldValue: string, _newValue: string) {
    this.#render()
  }

  #findProvider() {
    // Search up the DOM tree for the nearest app-i18n-provider
    this.#provider = this.closest('app-i18n-provider') as AppI18nProvider | null

    if (!this.#provider) {
      console.error(
        '[AppI18nT] No <app-i18n-provider> found in parent chain. Please wrap this component in a provider.',
      )
    }
  }

  #render() {
    const key = this.getAttribute('key')
    if (!key) {
      console.warn('[AppI18nT] No key attribute provided')
      this.textContent = ''
      return
    }

    if (!this.#provider) {
      this.#findProvider()
      if (!this.#provider) {
        this.textContent = key
        return
      }
    }

    // Parse variables if provided
    let vars: Record<string, any> | undefined
    const varsAttr = this.getAttribute('vars')
    if (varsAttr) {
      try {
        vars = JSON.parse(varsAttr)
      } catch (error) {
        console.error('[AppI18nT] Failed to parse vars JSON', error)
      }
    }

    // Get translation from provider
    const translation = this.#provider.getTranslation(key, vars)
    this.textContent = translation
  }

  /**
   * Public method to force refresh the translation
   * Useful when provider's translations change
   */
  public refresh() {
    this.#render()
  }

  disconnectedCallback() {
    // Cleanup if needed
  }
}

customElements.define('app-i18n-t', AppI18nT)
